import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { InvoiceQueryMetadata } from '~/api/invoices';
import type { Invoice, InvoiceStatus, SubmittedInvoice } from '~/api/invoices/types';
import type { Brand } from '~/types/user';

import { useClientsWithTrashed } from '~/api/clients';
import { invoicesQueryOptions, useInvoiceFilters, useInvoices } from '~/api/invoices';
import { useUser } from '~/api/user';
import { createAppColumnHelper, Table } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import { useIntl } from '~/hooks/useIntl';
import { includes } from '~/utils/arrays';

import {
  useInvoicesTableColumnFilters,
  useInvoicesTableGlobalFilter,
  useInvoicesTablePaginationState,
  useInvoicesTableSorting,
} from './InvoicesTable.nuqs';
import { InvoiceAdvantageCell } from './InvoicesTableCells/InvoiceAdvantageCell';
import { InvoiceClientCell } from './InvoicesTableCells/InvoiceClientCell';
import { InvoiceDocumentDateCell } from './InvoicesTableCells/InvoiceDocumentDateCell';
import { InvoiceMobileCell } from './InvoicesTableCells/InvoiceMobileCell';
import { InvoiceOptionsCell } from './InvoicesTableCells/InvoiceOptionsCell';
import { InvoiceStatusCell } from './InvoicesTableCells/InvoiceStatusCell';
import { InvoiceTitleCell } from './InvoicesTableCells/InvoiceTitleCell';
import { InvoiceTotalCell } from './InvoicesTableCells/InvoiceTotalCell';

const columnHelper = createAppColumnHelper<Invoice>();

type Scope = {
  clientId?: number | null;
  invoiceTemplateId?: number | null;
};

export const useInvoicesQueryMetadata = ({ clientId = null, invoiceTemplateId = null }: Scope): InvoiceQueryMetadata => {
  const [pagination] = useInvoicesTablePaginationState();
  const [sorting] = useInvoicesTableSorting();
  const [globalFilter] = useInvoicesTableGlobalFilter();
  const [columnFilters] = useInvoicesTableColumnFilters();

  const [debouncedGlobalFilter] = useDebouncedState(globalFilter, 200);

  return useMemo(
    () => ({
      pagination,
      sorting,
      filters: {
        search: debouncedGlobalFilter.trim(),
        clientId: clientId ?? columnFilters.find((filter) => filter.id === ColumnIds.invoicesClient)?.value ?? null,
        brandId: columnFilters.find((filter) => filter.id === ColumnIds.invoicesBrand)?.value ?? null,
        year: columnFilters.find((filter) => filter.id === ColumnIds.invoicesDocYear)?.value ?? null,
        quarter: columnFilters.find((filter) => filter.id === ColumnIds.invoicesDocQuarter)?.value ?? null,
        status: (columnFilters.find((filter) => filter.id === ColumnIds.invoicesStatus)?.value ?? null) as
          SubmittedInvoice['status'][] | null,
        invoiceTemplateId,
      },
    }),
    [pagination, sorting, debouncedGlobalFilter, clientId, columnFilters, invoiceTemplateId],
  );
};

export const useInvoicesTotalResults = (scope: Scope): number | undefined =>
  useInvoices(useInvoicesQueryMetadata(scope)).data?.pagination.totalResults;

export const useInvoicesTableColumns = (isPinnedToClient: boolean) => {
  const { data: clients } = useClientsWithTrashed();
  const filters = useInvoicesTableFilters();

  const { t } = useTranslation(['common', 'invoices']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Selector
        columnHelper.display({
          id: ColumnIds.selector,
          header: () => <Table.SelectAll />,
          cell: ({ row }) => <Table.SelectRow row={row} />,
          meta: {
            styles: { size: 'max-content' },
          },
        }),

        // Title & entry numbers
        // Note: old invoices only have a description instead of title
        columnHelper.accessor(({ description, title }) => title || description, {
          id: ColumnIds.invoicesTitle,
          header: t('invoices:overview.columns.entryNumber'),
          cell: ({ row: { original: invoice } }) => <InvoiceTitleCell invoice={invoice} />,
          meta: {
            styles: { size: '3fr', minSize: 125 },
            isResizable: true,
          },
        }),

        // Brand (hidden, column filter only)
        columnHelper.accessor(({ brandId }) => brandId, {
          id: ColumnIds.invoicesBrand,
          meta: {
            filterOptions: filters.brands,
          },
        }),

        // Client
        columnHelper.accessor(({ clientId }) => clients.find((client) => client.id === clientId)?.name ?? '', {
          id: ColumnIds.invoicesClient,
          header: t('invoices:overview.columns.client'),
          cell: ({ row: { original: invoice } }) => <InvoiceClientCell invoice={invoice} />,
          meta: {
            styles: { size: '2fr', minSize: 125 },
            isResizable: true,
            filterOptions: filters.clients,
          },
        }),

        // Document date
        columnHelper.accessor(({ documentDate }) => documentDate, {
          id: ColumnIds.invoicesDocDate,
          header: t('invoices:overview.columns.date'),
          cell: ({ row: { original: invoice } }) => <InvoiceDocumentDateCell invoice={invoice} />,
          meta: {
            styles: { size: 'max-content', minSize: 100 },
            isResizable: true,
          },
        }),

        // Doc year (hidden, column filter only)
        columnHelper.accessor(({ documentDate }) => dayjs(documentDate).year(), {
          id: ColumnIds.invoicesDocYear,
          meta: {
            filterOptions: filters.years,
          },
        }),

        // Doc quarter (hidden, column filter only)
        columnHelper.accessor(({ documentDate }) => `${dayjs(documentDate).year()} ${dayjs(documentDate).quarter()}`, {
          id: ColumnIds.invoicesDocQuarter,
          meta: {
            filterOptions: filters.quarters,
          },
        }),

        // Status
        columnHelper.accessor(({ status }) => status, {
          id: ColumnIds.invoicesStatus,
          header: t('invoices:overview.columns.status.title'),
          cell: ({ row: { original: invoice } }) => <InvoiceStatusCell invoice={invoice} />,
          meta: {
            styles: { size: 'max-content', minSize: 120 },
            filterOptions: filters.statuses,
          },
        }),

        // Total
        columnHelper.accessor(({ calculationData }) => calculationData.fcExclVat, {
          id: ColumnIds.invoicesTotal,
          header: t('invoices:overview.columns.total'),
          cell: ({ getValue }) => <InvoiceTotalCell value={getValue()} />,
          meta: {
            styles: { size: 'max-content', minSize: 100, justify: 'end' },
          },
        }),

        // Advantage
        columnHelper.accessor((invoice) => invoice.calculationData.advantageWithCs?.advantage, {
          id: ColumnIds.invoicesAdvantage,
          header: t('invoices:overview.columns.saved'),
          cell: ({ getValue, row: { original: invoice } }) => {
            const advantage = getValue();

            if (!advantage || includes(['Draft', 'InReview', 'LockedByUser', 'Credited'] satisfies InvoiceStatus[], invoice.status)) {
              return '-';
            }

            return <InvoiceAdvantageCell advantage={advantage} invoice={invoice} />;
          },
          meta: {
            styles: { size: 'max-content', minSize: 100, justify: 'end' },
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: invoice } }) => <InvoiceOptionsCell invoice={invoice} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.invoicesMobile,
          cell: ({ row: { original: invoice } }) => <InvoiceMobileCell invoice={invoice} showClient={!isPinnedToClient} />,
          meta: { mobile: true },
        }),
      ]),
    [clients, filters, isPinnedToClient, t],
  );
};

export const usePrefetchNextPage = (nextPage: number | null, queryMetadata: InvoiceQueryMetadata) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (nextPage === null) return;

    queryClient.prefetchQuery(
      invoicesQueryOptions({
        ...queryMetadata,
        pagination: { ...queryMetadata.pagination, pageIndex: queryMetadata.pagination.pageIndex + 1 },
      }),
    );
  }, [nextPage, queryClient, queryMetadata]);
};

const useInvoicesTableFilters = () => {
  const { data: user } = useUser();
  const { data: clients } = useClientsWithTrashed();
  const { data: invoiceFilters } = useInvoiceFilters();

  const { compareForSort } = useIntl();
  const { t } = useTranslation(['filters', 'invoices']);

  const clientFilterOptions = useMemo(
    () =>
      invoiceFilters.clientIds
        .map((clientId) => ({
          value: clientId,
          label: clients.find((client) => client.id === clientId)?.name ?? '',
        }))
        .sort((a, b) => compareForSort(a.label, b.label)),
    [clients, compareForSort, invoiceFilters.clientIds],
  );

  const brandFilterOptions = useMemo(
    () =>
      invoiceFilters.brandIds
        .map((brandId) => user.brands.find((brand) => brand.id === brandId) ?? null)
        .filter((brand): brand is Brand => brand !== null)
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || compareForSort(a.companyName, b.companyName))
        .map((brand) => ({ value: brand.id, label: brand.companyName })),
    [compareForSort, invoiceFilters.brandIds, user.brands],
  );

  const yearFilterOptions = useMemo(
    () =>
      invoiceFilters.years
        .map((year) => ({
          value: year,
          label: `${year}`,
        }))
        .sort((a, b) => -1 * compareForSort(a.label, b.label)),
    [invoiceFilters.years, compareForSort],
  );

  const quarterFilterOptions = useMemo(
    () =>
      invoiceFilters.quarters
        .map((yearQuarter) => ({
          value: yearQuarter,
          label: t('filters:quarter.option', { year: yearQuarter.split('-')[0], quarter: yearQuarter.split('-')[1] }),
        }))
        .sort((a, b) => {
          const [yearA, quarterA] = a.value.split('-').map((value) => +value);
          const [yearB, quarterB] = b.value.split('-').map((value) => +value);

          return yearB === yearA ? quarterB - quarterA : yearB - yearA;
        }),
    [invoiceFilters.quarters, t],
  );

  const statusFilterOptions = useMemo(
    () =>
      invoiceFilters.statuses
        .map((status) => ({
          value: status,
          label: t(`invoices:overview.columns.status.options.${status}`),
        }))
        .sort((a, b) => {
          const order: InvoiceStatus[] = ['InReview', 'LockedByUser', 'Approved', 'Expired', 'CoPaid', 'FcPaid', 'Credited'];
          return order.indexOf(a.value) - order.indexOf(b.value);
        }),
    [invoiceFilters.statuses, t],
  );

  return useMemo(
    () => ({
      clients: clientFilterOptions,
      brands: brandFilterOptions,
      years: yearFilterOptions,
      quarters: quarterFilterOptions,
      statuses: statusFilterOptions,
    }),
    [brandFilterOptions, clientFilterOptions, quarterFilterOptions, statusFilterOptions, yearFilterOptions],
  );
};
